var __importDefault = (this && this.__importDefault) || function (mod) {
  return (mod && mod.__esModule) ? mod : { "default": mod };
};
import * as React from 'react';
import { useMemo, useState } from 'react';
import { Checkbox, DefaultButton, Dropdown, Link, PrimaryButton, Stack, Text, } from '@fluentui/react';
import { countByMonthForYear, filterByEventTypes, getYears, groupByManager, groupByResource, MONTH_NAMES, } from './pendingEvents';
const fmtDate = (s) => {
    const d = new Date(s);
    return isNaN(d.getTime()) ? s : d.toLocaleDateString('en-us');
};
const inYearMonth = (it, year, month) => {
    const d = new Date(it.StartDate);
    if (isNaN(d.getTime()) || d.getFullYear() !== year) {
        return false;
    }
    return month === 0 ? true : d.getMonth() + 1 === month;
};
const resourceLabel = (items, key, showName) => {
    var _a;
    const first = items[0];
    return showName && first && first.FirstName
        ? `${first.FirstName} ${(_a = first.LastName) !== null && _a !== void 0 ? _a : ''}`.trim()
        : key;
};
const EventRow = ({ event, selectable, selected, onToggleSelect, onApprove, onReject }) => (React.createElement("div", { "data-testid": `pe-event-${event.Id}`, style: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '6px 0 6px 8px',
        marginBottom: 6,
        borderLeft: `3px solid ${event.EventColorName || '#29c7ee'}`,
    } },
    React.createElement(Stack, { horizontal: true, verticalAlign: "center", tokens: { childrenGap: 10 } },
        selectable ? (React.createElement(Checkbox, { checked: !!selected, ariaLabel: `Select ${event.EventTypeName} for ${event.ResourceId}`, onChange: () => onToggleSelect === null || onToggleSelect === void 0 ? void 0 : onToggleSelect(event) })) : null,
        React.createElement("div", null,
            React.createElement("div", null,
                React.createElement("strong", null, event.EventTypeName),
                event.Comments ? ` : ${event.Comments}` : ''),
            React.createElement("div", { style: { fontSize: 12, color: '#605e5c' } },
                fmtDate(event.StartDate),
                " \u2013 ",
                fmtDate(event.EndDate),
                event.TimeZone ? ` (${event.TimeZone})` : ''))),
    React.createElement(Stack, { horizontal: true, tokens: { childrenGap: 8 } },
        React.createElement(PrimaryButton, { text: "Approve", ariaLabel: `Approve ${event.EventTypeName} for ${event.ResourceId}`, onClick: () => onApprove === null || onApprove === void 0 ? void 0 : onApprove(event) }),
        React.createElement(DefaultButton, { text: "Reject", ariaLabel: `Reject ${event.EventTypeName} for ${event.ResourceId}`, onClick: () => onReject === null || onReject === void 0 ? void 0 : onReject(event) }))));
const ResourceGroup = ({ label, events, selectable, selectedIds, onToggleSelect, onApprove, onReject }) => (React.createElement("div", { style: { marginBottom: 12 } },
    React.createElement("div", { style: { fontWeight: 600, marginBottom: 4 } }, label),
    events.map((e) => (React.createElement(EventRow, { key: String(e.Id), event: e, selectable: selectable, selected: !!selectedIds && selectedIds.indexOf(e.Id) !== -1, onToggleSelect: onToggleSelect, onApprove: onApprove, onReject: onReject })))));
/**
 * FM pending-events list (F3) — React port of `pendingevents.html` + the grouping in
 * `pendingevent-actions.controller.js`. Shows My + Delegated pending OOF approvals grouped by
 * resource, filtered by year / month / event type, each with Approve/Reject. The actual
 * approve/reject workflow (ManageApprovalRequests, PATCH) is Phase 4 — surfaced here as callbacks.
 * Data is injected, so it renders/tests without the FXP shell.
 */
export const PendingEventsList = ({ myItems, delegatedItems = [], eventTypes = [], onApprove, onReject, selectable, selectedIds, onToggleSelect, }) => {
    var _a;
    const allItems = useMemo(() => [...myItems, ...delegatedItems], [myItems, delegatedItems]);
    const years = useMemo(() => getYears(allItems), [allItems]);
    const defaultYear = years.indexOf(new Date().getFullYear()) > -1
        ? new Date().getFullYear()
        : (_a = years[0]) !== null && _a !== void 0 ? _a : new Date().getFullYear();
    const [selectedYear, setSelectedYear] = useState(defaultYear);
    // F3 parity: default to the current month when it has items in the default year; else ALL months.
    const initialMonth = useMemo(() => {
        const now = new Date();
        if (defaultYear !== now.getFullYear()) {
            return 0;
        }
        const m = now.getMonth() + 1;
        const hasItems = allItems.some((it) => {
            const d = new Date(it.StartDate);
            return !isNaN(d.getTime()) && d.getFullYear() === defaultYear && d.getMonth() + 1 === m;
        });
        return hasItems ? m : 0;
    }, [allItems, defaultYear]);
    const [selectedMonth, setSelectedMonth] = useState(initialMonth); // 0 = all months
    const [selectedTypes, setSelectedTypes] = useState([]);
    const [showName, setShowName] = useState(false);
    const monthChips = useMemo(() => countByMonthForYear(allItems, selectedYear, selectedTypes), [allItems, selectedYear, selectedTypes]);
    const myGroups = useMemo(() => {
        const filtered = filterByEventTypes(myItems.filter((i) => inYearMonth(i, selectedYear, selectedMonth)), selectedTypes);
        return groupByResource(filtered);
    }, [myItems, selectedYear, selectedMonth, selectedTypes]);
    const myCount = useMemo(() => Object.values(myGroups).reduce((n, arr) => n + arr.length, 0), [myGroups]);
    const delegatedByManager = useMemo(() => {
        const filtered = filterByEventTypes(delegatedItems.filter((i) => inYearMonth(i, selectedYear, selectedMonth)), selectedTypes);
        const byMgr = groupByManager(filtered);
        return Object.keys(byMgr).map((mgr) => ({
            manager: mgr,
            count: byMgr[mgr].length,
            resources: groupByResource(byMgr[mgr]),
        }));
    }, [delegatedItems, selectedYear, selectedMonth, selectedTypes]);
    const typeOptions = eventTypes.map((t) => ({
        key: t.EventTypeName.toLowerCase(),
        text: t.EventTypeName,
    }));
    const monthLabel = selectedMonth === 0 ? 'ALL MONTHS' : MONTH_NAMES[selectedMonth];
    return (React.createElement(Stack, { tokens: { childrenGap: 12 }, styles: { root: { padding: 20 } }, "data-testid": "pending-events-list" },
        years.length > 0 ? (React.createElement(Stack, { horizontal: true, tokens: { childrenGap: 8 }, verticalAlign: "center" }, years.map((y) => (React.createElement(Link, { key: y, "data-testid": `pe-year-${y}`, "aria-label": `Year ${y}`, styles: { root: { fontWeight: y === selectedYear ? 700 : 400 } }, onClick: () => {
                setSelectedYear(y);
                setSelectedMonth(0);
            } }, y))))) : null,
        React.createElement(Stack, { horizontal: true, wrap: true, tokens: { childrenGap: 12 }, "data-testid": "pe-month-chips" },
            React.createElement(Link, { "data-testid": "pe-month-all", styles: { root: { fontWeight: selectedMonth === 0 ? 700 : 400 } }, onClick: () => setSelectedMonth(0) }, "ALL"),
            monthChips.map((c) => (React.createElement(Link, { key: c.month, "data-testid": `pe-month-${c.month}`, styles: { root: { fontWeight: selectedMonth === c.month ? 700 : 400 } }, onClick: () => setSelectedMonth(c.month) },
                c.monthName,
                "(",
                c.count,
                ")")))),
        React.createElement(Stack, { horizontal: true, horizontalAlign: "space-between", verticalAlign: "center", wrap: true, tokens: { childrenGap: 12 } },
            React.createElement(Stack, { horizontal: true, tokens: { childrenGap: 6 }, verticalAlign: "center" },
                React.createElement(Link, { "data-testid": "pe-toggle-alias", styles: { root: { fontWeight: !showName ? 700 : 400 } }, onClick: () => setShowName(false) }, "ALIAS"),
                React.createElement(Text, null, "|"),
                React.createElement(Link, { "data-testid": "pe-toggle-name", styles: { root: { fontWeight: showName ? 700 : 400 } }, onClick: () => setShowName(true) }, "NAME")),
            typeOptions.length > 0 ? (React.createElement(Dropdown, { placeholder: "All Event Types", multiSelect: true, options: typeOptions, selectedKeys: selectedTypes, styles: { root: { minWidth: 220 } }, ariaLabel: "Filter by event type", onChange: (_e, option) => {
                    if (!option) {
                        return;
                    }
                    const key = option.key;
                    setSelectedTypes((prev) => option.selected ? [...prev, key] : prev.filter((k) => k !== key));
                } })) : null),
        React.createElement(Text, { variant: "large", as: "h3" },
            "EVENT APPROVAL - ",
            monthLabel,
            " ",
            selectedYear),
        React.createElement("div", { "data-testid": "pe-my-approvals" },
            React.createElement("div", { style: { fontWeight: 600, background: '#d7e2e8', padding: '6px 10px' } },
                "My Approvals (",
                myCount,
                ")"),
            myCount === 0 ? (React.createElement(Text, { styles: { root: { padding: 8 } } }, "No pending approvals.")) : (Object.keys(myGroups).map((key) => (React.createElement(ResourceGroup, { key: key, label: resourceLabel(myGroups[key], key, showName), events: myGroups[key], selectable: selectable, selectedIds: selectedIds, onToggleSelect: onToggleSelect, onApprove: onApprove, onReject: onReject }))))),
        delegatedByManager.map((mgr) => (React.createElement("div", { key: mgr.manager, "data-testid": `pe-delegated-${mgr.manager}` },
            React.createElement("div", { style: { fontWeight: 600, background: '#d7e2e8', padding: '6px 10px' } },
                mgr.manager,
                "'s Approvals (",
                mgr.count,
                ")"),
            Object.keys(mgr.resources).map((key) => (React.createElement(ResourceGroup, { key: key, label: resourceLabel(mgr.resources[key], key, showName), events: mgr.resources[key], selectable: selectable, selectedIds: selectedIds, onToggleSelect: onToggleSelect, onApprove: onApprove, onReject: onReject }))))))));
};
export default PendingEventsList;
//# sourceMappingURL=PendingEventsList.js.map